import { useOidc } from "@axa-fr/react-oidc";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { toast } from "sonner";
import { GlobalRole } from "@/api/session";
import { useSession } from "@/auth/session-provider";
import { Avatar, AvatarBadge, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotifications } from "@/features/notifications/notification-provider";
import type { TranslationKey } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { identityLabelOf, initialsOf } from "@/utils/person-name";

const GLOBAL_ROLE_LABEL_KEYS: Record<GlobalRole, TranslationKey> = {
  [GlobalRole.Admin]: "user.role.admin",
  [GlobalRole.Viewer]: "user.role.viewer",
  [GlobalRole.ScopeAll]: "user.role.scopeAll",
};

export function CurrentUserMenu() {
  const { agent } = useSession();
  const { logout } = useOidc();
  const { webPush } = useNotifications();
  const { t } = useI18n();
  const [loggingOut, setLoggingOut] = useState(false);

  const initials = initialsOf(agent.displayName);
  const roleLabel = agent.globalRoles.map((role) => t(GLOBAL_ROLE_LABEL_KEYS[role])).join(" · ");
  // 没有全局角色的人只有组内角色，用登录名当副标题比留空更有用。
  const identityLabel = identityLabelOf(agent);
  const subtitle = roleLabel || identityLabel || t("user.supportAgent");

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await webPush.detachForLogout();
    } catch {
      // Web Push cleanup is best-effort and must not block sign-out.
    }

    try {
      // 回到根路径，下次进来会重新走一遍 OidcSecure。
      await logout("/");
    } catch {
      setLoggingOut(false);
      toast.error(t("user.signOutFailed"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button aria-label={t("user.openMenu")} className="h-10 gap-2 rounded-xl px-2" variant="ghost">
          <Avatar size="sm">
            {agent.avatarUrl ? <AvatarImage alt="" src={agent.avatarUrl} /> : null}
            <AvatarFallback>{initials}</AvatarFallback>
            <AvatarBadge />
          </Avatar>
          <span className="hidden max-w-32 truncate text-sm font-medium lg:inline">{agent.displayName}</span>
          <Icon className="hidden size-4 text-muted-foreground lg:block" icon="tabler:chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3">
            <Avatar>
              {agent.avatarUrl ? <AvatarImage alt="" src={agent.avatarUrl} /> : null}
              <AvatarFallback>{initials}</AvatarFallback>
              <AvatarBadge />
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{agent.displayName}</p>
              <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        {identityLabel ? <p className="px-2 pb-1.5 text-xs text-muted-foreground truncate">{identityLabel}</p> : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Icon icon="tabler:user-circle" />
          {t("user.profile")}
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Icon icon="tabler:settings" />
          {t("user.accountSettings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={loggingOut} onSelect={handleLogout}>
          <Icon icon="tabler:logout" />
          {loggingOut ? t("user.signingOut") : t("user.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
